import React, { useState, useEffect, useId } from 'react';
import PropTypes from 'prop-types';
import {
  buildStandaloneSearchBox,
  loadAdvancedSearchQueryActions
} from '@coveo/headless';

const StandaloneSearchBox = ({
  engine,
  searchBtnLabel,
  searchInputPlaceholder,
  redirectUrl,
  coveoSource
}) => {
  const [controller, setController] = useState(null);
  const [state, setState] = useState(null);
  const isEnterKey = e => e.key === 'Enter';

  const id = useId();

  const advancedQuerySearchActionCreator =
    loadAdvancedSearchQueryActions(engine);

  const minQueryLengthForSuggestions = 3;

  const closeMenu = () => {
    document.querySelector('.primary-navigation__megamenu-wrapper').classList.remove('active');
  }


  useEffect(() => {
    engine?.dispatch(
      advancedQuerySearchActionCreator.registerAdvancedSearchQueries({
        aq: `@source/="${coveoSource}"`
      })
    );

    const _controller = buildStandaloneSearchBox(engine, {
      options: {
        id: 'ssb-1',
        redirectionUrl: redirectUrl
      }
    });
    setController(_controller);
    setState(_controller.state);
    _controller.subscribe(() => {
      setState(_controller.state);
    });
  }, []);

  const suggestionStyle = {
    cursor: 'pointer',
  };

  const handleSuggestionClick = (e, suggestion) => {
    e.preventDefault();
    controller.selectSuggestion(suggestion);
  };

  if (!state) {
    return null;
  }

  if (state.redirectTo) {
    const { redirectTo, value, analytics } = state;
    const data = JSON.stringify({ value, analytics });
    localStorage.setItem('coveo_standalone_search_box_data', data);
    window.location.href = redirectTo;
    return null;
  }

  const submitQuery = () => {
    localStorage.setItem(
      'coveo_standalone_search_box_data',
      JSON.stringify(state.value)
    );

    controller.submit();
  };

  const handleKeyDown = e => {
    if (isEnterKey(e)) {
      closeMenu();
      submitQuery();
    }
  };

  const handleSuggestionsKeyDown = e => {
    if (
      e.key === 'ArrowDown' &&
      state.suggestions.length > 0 &&
      state.value?.length >= minQueryLengthForSuggestions
    ) {
      e.preventDefault();
      const focusedElement = document.querySelector(
        '.header-search__search-bar .coveo-search-results__search-box-suggestion:focus'
      );
      const firstSuggestion = document.querySelector(
        '.header-search__search-bar .coveo-search-results__search-box-suggestion:first-child'
      );
      const lastSuggestion = document.querySelector(
        '.header-search__search-bar .coveo-search-results__search-box-suggestion:last-child'
      );

      if (focusedElement === null) {
        firstSuggestion.focus();
      } else if (focusedElement === lastSuggestion) {
        // firstSuggestion.focus();
        document
          .getElementsByClassName('standalone-searchbox__input').item(0)
          .focus();
      } else {
        focusedElement.nextSibling.focus();
      }
    }

    if (
      e.key === 'ArrowUp' &&
      state.suggestions.length > 0 &&
      state.value.length >= minQueryLengthForSuggestions
    ) {
      e.preventDefault();
      const focusedElement = document.querySelector(
        '.header-search__search-bar .coveo-search-results__search-box-suggestion:focus'
      );
      const firstSuggestion = document.querySelector(
        '.header-search__search-bar .coveo-search-results__search-box-suggestion:first-child'
      );
      const lastSuggestion = document.querySelector(
        '.header-search__search-bar .coveo-search-results__search-box-suggestion:last-child'
      );

      if (focusedElement === null) {
        lastSuggestion.focus();
      } else if (focusedElement === firstSuggestion) {
        // lastSuggestion.focus();
        document
          .getElementsByClassName('standalone-searchbox__input').item(0)
          .focus();
      } else {
        focusedElement.previousSibling.focus();
      }
    }
    
  }

  const handleSearchButtonClick = () => {
    closeMenu();
    submitQuery(); 
  };

  const handleClearButtonClick = () => {
    controller.clear();
  };

  const handleInputChange = e => {
    controller.updateText(e.target.value);
  };

  return (
    <>
      {state && (
        <div className="standalone-searchbox">
          <div className="standalone-searchbox__input-container">
            <div 
            className="standalone-searchbox__input-group"
            onKeyDown={e => { handleSuggestionsKeyDown(e) }}
            >
              <input
                id={id}
                className="standalone-searchbox__input"
                value={state.value}
                onChange={handleInputChange}
                onKeyDown={e => handleKeyDown(e)}
                placeholder={searchInputPlaceholder}
                aria-label={searchInputPlaceholder}
                autoComplete='off'
              />
              <div className="standalone-searchbox__search-actions">
                <button
                  type="button"
                  className={
                    state.value.length >= 1
                      ? 'standalone-searchbox__clear'
                      : 'standalone-searchbox__clear standalone-searchbox__clear--disabled'
                  }
                  aria-label={searchBtnLabel}
                  disabled={!(state.value.length >= 1)}
                  onClick={handleClearButtonClick}
                >
                  <svg>
                    <use xlinkHref="#close" />
                  </svg>
                </button>
                <button
                  type="submit"
                  className={
                    state.value.length >= 1
                      ? 'standalone-searchbox__submit'
                      : 'standalone-searchbox__submit standalone-searchbox__submit--disabled'
                  }
                  aria-label={searchBtnLabel}
                  disabled={!(state.value.length >= 1)}
                  onClick={handleSearchButtonClick}
                >
                  <svg>
                    <use xlinkHref="#search-icon" />
                  </svg>
                </button>
                
              </div>
              {state.suggestions.length > 0 &&
                  state.value.length >= minQueryLengthForSuggestions && (
                  <div className="coveo-search-results__search-box-suggestion-list">
                    {state.suggestions.map(suggestion => (
                      <button
                        className="coveo-search-results__search-box-suggestion"
                        style={suggestionStyle}
                        key={suggestion.rawValue}
                        onClick={e => handleSuggestionClick(e, suggestion.rawValue)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            handleSuggestionClick(e, suggestion.rawValue);
                          }
                        }}
                        dangerouslySetInnerHTML={{
                          __html: suggestion.highlightedValue
                        }}
                        onMouseDown={e => e.preventDefault()}
                      ></button>
                    ))}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

StandaloneSearchBox.defaultProps = {
  searchBtnLabel: 'Search',
  searchInputPlaceholder: 'Search MCPHS',
  redirectUrl: '/search'
};

StandaloneSearchBox.propTypes = {
  engine: PropTypes.object,
  searchBtnLabel: PropTypes.string,
  searchInputPlaceholder: PropTypes.string,
  redirectUrl: PropTypes.string,
  coveoSource: PropTypes.string
};

export default StandaloneSearchBox;
